import React from "react";
import { MapPin, GraduationCap, BookOpen, Laptop, LibraryBig, GitPullRequest, Users } from "lucide-react";
import { education } from "../mock";
import Reveal from "./Reveal";
import SectionHeader from "./SectionHeader";
import SpotlightCard from "./SpotlightCard";

const learningWays = [
  { icon: Laptop, label: "Online Courses" },
  { icon: LibraryBig, label: "Research Papers" },
  { icon: GitPullRequest, label: "Open Source" },
  { icon: Users, label: "Tech Communities" },
];

const Education = () => {
  return (
    <section id="education" className="relative pt-8 md:pt-10 pb-10 md:pb-12 bg-secondary/30">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="05."
          eyebrow="Education"
          title="Academic foundation"
          description="A strong base in computer science, mathematics, and AI — continuously reinforced by hands-on learning."
        />

        <div className="relative ml-3 md:ml-6">
          {/* Timeline line */}
          <div className="absolute left-0 md:left-6 top-2 bottom-2 w-px bg-gradient-to-b from-primary via-emerald-500/50 to-teal-400/30" />

          <div className="space-y-8 md:space-y-10">
            {education.map((edu, index) => (
              <Reveal key={index} delay={index * 120} className="relative md:pl-20">
                {/* Node */}
                <div className="absolute left-0 md:left-6 top-6 md:top-8 -translate-x-1/2 md:translate-x-0 flex items-center justify-center">
                  <span className="relative grid h-7 w-7 md:h-8 md:w-8 place-items-center rounded-full bg-card border-2 border-primary shadow-lg shadow-primary/20">
                    <span className="h-2 w-2 rounded-full bg-primary" />
                    <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping" style={{ animationDuration: "2.5s" }} />
                  </span>
                </div>

                <SpotlightCard className="cursor-target ml-10 md:ml-0 rounded-2xl border border-border bg-card/80 backdrop-blur-sm transition-all duration-300 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5" color="rgba(45,212,191,0.12)">
                  <div className="relative p-6 md:p-8">
                    <div className="absolute top-0 left-6 right-6 h-[2px] rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 scale-x-0 transition-transform duration-500 origin-left group-hover/spot:scale-x-100" />
                    {/* header row */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span
                      className={
                        edu.status === "Currently Pursuing"
                          ? "inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-3 py-1 text-xs font-semibold"
                          : "inline-flex items-center rounded-full bg-primary/10 text-primary border border-primary/20 px-3 py-1 text-xs font-semibold"
                      }
                    >
                      {edu.status === "Currently Pursuing" && (
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                        </span>
                      )}
                      {edu.status}
                    </span>
                    <span className="font-mono text-xs md:text-sm text-muted-foreground">
                      {edu.duration}
                    </span>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="hidden md:inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md">
                      <GraduationCap size={20} />
                    </span>
                    <div>
                      <h4 className="font-display text-lg md:text-xl font-semibold leading-snug">
                        {edu.degree}
                      </h4>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          <BookOpen size={14} className="text-primary" />
                          {edu.institution}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin size={14} className="text-primary" />
                          {edu.location}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                </SpotlightCard>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Continuous learning */}
        <Reveal delay={150} className="mt-12 md:mt-16">
          <div className="glow-border rounded-2xl bg-card/60 backdrop-blur-sm p-6 md:p-7">
            <div className="flex items-center gap-2 font-display text-lg font-semibold mb-2">
              <span className="text-primary">
                <GraduationCap size={20} />
              </span>
              Beyond the classroom
            </div>
            <p className="text-sm md:text-base text-muted-foreground mb-5">
              I actively invest in self-directed learning through hands-on projects and communities:
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {learningWays.map((way, index) => (
                <div
                  key={index}
                  className="cursor-target flex items-center gap-3 rounded-xl border border-border/70 bg-secondary/40 px-4 py-3.5 transition-all duration-300 hover:-translate-y-0.5 hover:bg-secondary/70"
                >
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500/15 to-emerald-500/15 text-primary">
                    <way.icon size={17} />
                  </span>
                  <span className="text-sm font-medium">{way.label}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Education;